import { Button, Progress } from 'antd';

import { ARROW_COUNTERCLOCKWISE, CHEVRON_LEFT, CHEVRON_RIGHT, CLOSE } from '../icons';
import { FileChip, FilePicture } from './FileChip';

/**
 * One file in the item form's file field (components/common/FileUploadField):
 * a picture card for a "Photos only" element, a row for any file. Four
 * states — uploading (progress), ready, removed ("Removed when you save",
 * with Undo: nothing is deleted before Save) and unknown (an id the form has
 * no facts for). A list offers Move earlier / Move later; every action keeps
 * the 44 px floor on a touch screen (data-types.css).
 */
export default function FileUploadItem({
	entry,
	picture = false,
	disabled = false,
	canMove = false,
	isFirst = false,
	isLast = false,
	onRemove,
	onUndo,
	onMove,
}) {
	const { file, source, state, percent, name } = entry;
	const label = file?.originalName || name || 'File';
	const classes = [
		'file-upload-item',
		picture ? 'file-upload-item--card' : 'file-upload-item--row',
		state === 'removed' ? 'file-upload-item--removed' : '',
	].filter(Boolean).join(' ');

	const visual = state === 'uploading' ? (
		<span className="file-upload-item__progress">
			<span className="file-upload-item__name">{label}</span>
			<Progress percent={percent || 0} size="small" showInfo={false} aria-label={`Uploading ${label}`} />
		</span>
	) : (picture && file ? (
		<FilePicture
			itemId={source?.itemId || null}
			heldIn={source?.heldIn || null}
			file={file}
			size="sm"
			className="file-upload-item__picture"
		/>
	) : <FileChip file={file || { originalName: label }} showSize={!picture} />);

	return (
		<div className={classes} data-testid="file-upload-item" data-state={state}>
			{visual}
			{state === 'removed' ? (
				<span className="file-upload-item__removed">
					<span>Removed when you save</span>
					<Button
						size="small"
						type="link"
						icon={<ARROW_COUNTERCLOCKWISE />}
						disabled={disabled}
						onClick={onUndo}
						aria-label={`Undo removing ${label}`}
					>
						Undo
					</Button>
				</span>
			) : null}
			{state === 'ready' || state === 'unknown' ? (
				<span className="file-upload-item__actions">
					{canMove ? (
						<>
							<Button
								size="small"
								icon={<CHEVRON_LEFT />}
								disabled={disabled || isFirst}
								onClick={() => onMove(-1)}
								aria-label={`Move ${label} earlier`}
							/>
							<Button
								size="small"
								icon={<CHEVRON_RIGHT />}
								disabled={disabled || isLast}
								onClick={() => onMove(1)}
								aria-label={`Move ${label} later`}
							/>
						</>
					) : null}
					<Button
						size="small"
						icon={<CLOSE />}
						disabled={disabled}
						onClick={onRemove}
						aria-label={`Remove ${label}`}
					/>
				</span>
			) : null}
		</div>
	);
}
